<template>
  <BContainer
    class="mt-2"
    fluid
  >
    <!-- Filters -->
    <BRow>
      <BCol
        cols="12"
        md="6"
        class="px-1"
      >
        <BFormGroup
          label="Search"
          label-for="filterHistorySearch"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistorySearch"
            v-model="tableHistories.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Status"
          label-for="filterHistoryStatus"
          label-class="font-weight-bolder"
        >
          <BFormSelect
            id="filterHistoryStatus"
            v-model="tableHistories.filter.status"
            :options="tableHistories.filterOptions.statuses"
            debounce="1000"
          >
            <template #first>
              <BFormSelectOption
                :value="null"
                disabled
              >
                -- select status here --
              </BFormSelectOption>
            </template>
          </BFormSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="6"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Ordered From"
          label-for="filterHistoryDateFrom"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistoryDateFrom"
            v-model="tableHistories.filter.date_from"
            :max="tableHistories.filter.date_to"
            debounce="1000"
            type="date"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="6"
        md="2"
        class="px-1"
        order="2"
      >
        <BFormGroup
          label="Ordered To"
          label-for="filterHistoryDateTo"
          label-class="font-weight-bolder"
        >
          <BFormInput
            id="filterHistoryDateTo"
            v-model="tableHistories.filter.date_to"
            :min="tableHistories.filter.date_from"
            debounce="1000"
            type="date"
          />
        </BFormGroup>
      </BCol>

    </BRow>

    <!-- Table -->
    <BRow
      v-if="Number(tabIndex) === 2"
      class="mt-1"
    >
      <BCol
        cols="12"
        class="px-1"
      >
        <BTable
          ref="tableHistories"
          small
          hover
          striped
          bordered
          responsive
          show-empty
          :items="tableProvider"
          :busy="tableHistories.busy"
          :filter="tableHistories.filter"
          :fields="tableHistories.fields"
          :stacked="tableHistories.stacked"
          :sort-by.sync="tableHistories.sortBy"
          :sort-desc.sync="tableHistories.sortDesc"
          :sort-direction="tableHistories.sortDirection"
          :filter-included-fields="tableHistories.filterOn"
          :current-page="tableHistories.currentPage"
          :per-page="tableHistories.perPage"
        >
          <template #cell(index)="{ index }">
            {{ tableRowNumber(tableHistories, index) }}
          </template>

          <template #row-details="row">
            <div class="p-2 bg-white">
              <BTable
                small
                hover
                bordered
                responsive
                show-empty
                class="col-md-6 col-12"
                :stacked="tableHistories.stacked"
                :items="row.item.transfer_details"
                :fields="[
                  { key: 'index', label: '#', class: 'text-center' },
                  { key: 'product_name', label: 'Product Name', class: 'col-md-6 col-12' },
                  { key: 'quantity', label: 'Ordered Quantity', class: 'text-center min-w-100px' },
                  { key: 'approved_quantity', label: 'Approved Quantity', class: 'text-center min-w-100px', formatter: approvedToPostingQuantity }
                ]"
              >
                <template #cell(index)="{ index }">
                  <div style="min-width: 25px;">
                    {{ tableRowNumber(tableHistories, index) }}
                  </div>
                </template>

                <template #cell(quantity)="{ value }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      class="font-weight-bolder"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>

                <template #cell(approved_quantity)="{ value, item }">
                  <div class="text-md-nowrap d-flex justify-content-center align-items-center min-h-35px">
                    <strong
                      :class="['font-weight-bolder', {
                        'text-danger': Number(value) <= 0,
                        'text-warning': Number(value) >= 1 && Number(item.quantity) !== Number(value),
                        'text-success': Number(item.quantity) === Number(value)
                      }]"
                      v-text="numberFormatter(value, '0,0')"
                    />
                  </div>
                </template>

              </BTable>
            </div>
          </template>

          <template #cell(action)="{ item, toggleDetails, detailsShowing }">
            <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-md-start justify-content-center gap-1">
              <BButton
                size="sm"
                variant="primary"
                :disabled="tableHistories.busy"
                @click.prevent="show(item)"
              >
                View
              </BButton>
              <BButton
                size="sm"
                :variant="detailsShowing ? 'danger' : 'warning' "
                :disabled="tableHistories.busy"
                @click.prevent="toggleDetails"
              >
                {{ detailsShowing ? 'Hide' : 'Show' }} Products
              </BButton>
            </div>
          </template>

          <template #cell(transaction_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(transfer_number)="{ value }">
            <b
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #cell(status)="{ value }">
            <b
              :class="[
                'min-w-80px',
                statusByVariant(value, 'text')
              ]"
              v-text="value"
            />
          </template>

          <template #cell()="{ value }">
            <div
              class="text-md-nowrap"
              v-text="value"
            />
          </template>

          <template #table-busy>
            <div class="text-center py-5">
              <BIcon
                icon="stopwatch"
                font-scale="5"
                animation="cylon"
              />
              <p class="h3 py-2">
                <strong>Loading . . .</strong>
              </p>
            </div>
          </template>

        </BTable>
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-2 px-1"
      >
        <BFormSelect
          v-model="tableHistories.perPage"
          :options="tableHistories.pageOptions"
          :disabled="tableHistories.busy"
          class="w-100 w-md-25"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1 px-1"
      >
        <BPagination
          v-model="tableHistories.currentPage"
          :total-rows="tableHistories.totalRows"
          :per-page="tableHistories.perPage"
          :disabled="tableHistories.busy"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>

    </BRow>
  </BContainer>
</template>
<script>
import MISC from '@/mixins/misc'
import FORMATTER from '@/mixins/formatter'

import { SPOfficerIBTransfer } from '@/services/po'

export default {

  name: 'VPOfficerIBTransferHistory',

  mixins: [
    MISC,
    FORMATTER
  ],

  props: {
    tabIndex: {
      type: Number,
      default: () => 0
    }
  },

  data () {
    return {
      tableHistories: {
        busy: false,
        filter: {
          search: '',
          status: this.$route.params?.status || 'all',
          date_from: '',
          date_to: ''
        },
        filterOptions: {
          statuses: [
            { value: 'all', text: 'All' },
            { value: 'closed', text: 'Closed' },
            { value: 'disapproved', text: 'Disapproved' },
            { value: 'canceled', text: 'Canceled' }
          ]
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 11, key: 'action', class: 'text-center' },
          { mobile: 1, key: 'created_at', label: 'Transferred At', class: 'min-w-175px', formatter: this.dateTimeShortFormatter, sortable: true },
          { mobile: 2, key: 'status', label: 'Transfer Status', class: 'text-center' },
          { mobile: 3, key: 'transaction_number', label: 'Transaction Number', class: 'text-center min-w-150px', sortable: true },
          { mobile: 4, key: 'transfer_number', label: 'Transfer Number', class: 'text-center min-w-150px', sortable: true },
          { mobile: 5, key: 'origin_name', label: 'Transfer For', class: 'min-w-200px' },
          { mobile: 6, key: 'destination_name', label: 'Transfer To', class: 'min-w-200px' },
          { mobile: 7, key: 'transfer_details.length', label: 'Products', class: 'text-center' },
          { mobile: 10, key: 'updated_at', class: 'min-w-175px', formatter: this.dateTimeShortFormatter }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableHistories.busy = true
      return await SPOfficerIBTransfer.histories({
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        filter_status: filter.status,
        filter_date_from: filter.date_from,
        filter_date_to: filter.date_to
      }).then(({ data }) => {
        this.tableHistories.totalRows = data.total_rows
        return data.items
      }).catch(() => {
        this.tableHistories.totalRows = 0
        return []
      }).finally(() => {
        this.tableHistories.busy = false
      })
    },

    tableRefresh () {
      this.$refs.tableHistories.refresh()
    },

    show (stockRequest) {
      this.$emit('show', stockRequest)
    },

    edit (stockRequest) {
      this.$emit('edit', stockRequest)
    }
  }
}
</script>
